import { CountryCode } from '@/constants/countryCodes';

/**
 * ACDEV-28461: the footer's legal links vary by the shopper's country. Japan
 * requires a Cookie Policy and an SCTA (特商法) notice, and drops the payment
 * Privacy Policy from the footer because the disclaimer below already links it.
 *
 * Labels normally follow the shopper's locale — the link id doubles as the i18n
 * key (`checkout.<id>` via `useTranslate`). A country entry may instead pin a
 * fixed `label`, which wins over the active locale; see the Japan entry.
 *
 * Only `privacyPolicy` and `refundPolicy` have `checkout.*` translations. Japan
 * is the sole source of `cookiePolicy` / `scta` and pins both labels, so no
 * translation exists for them: a country adding either WITHOUT a `label` would
 * render the raw key to the shopper. Pin the label or add the i18n entry first.
 */

/**
 * Every legal link the footer can render, in the order it must render.
 * Countries select a subset; they cannot reorder. Support is deliberately
 * absent — its href is generated per customer (`generateZendeskSupportURL`), so
 * `Footer` appends it after this list. Its LABEL still varies by country, via
 * {@link getFooterSupportLabel}.
 */
const FOOTER_LINK_ORDER = ['privacyPolicy', 'refundPolicy', 'cookiePolicy', 'scta'] as const;

export type FooterLinkId = (typeof FOOTER_LINK_ORDER)[number];

interface FooterLinkConfig {
  url: string;
  /**
   * Fixed link text that overrides the active locale. Use only where the label
   * is legally tied to the document the URL points at, so it must not vary with
   * the shopper's chosen language.
   */
  label?: string;
}

/** A link with no entry for a given country is not rendered there. */
type FooterLinkConfigs = Partial<Record<FooterLinkId, FooterLinkConfig>>;

const DEFAULT_FOOTER_LINKS: FooterLinkConfigs = {
  privacyPolicy: { url: 'https://appcharge.com/payments-privacy-policy/' },
  refundPolicy: { url: 'https://appcharge.com/refund-policy/' },
};

/**
 * Japan pins Japanese labels regardless of the shopper's locale: each one names
 * the specific Japanese-jurisdiction document its `-jp` URL points at, so an
 * English-locale shopper in Japan must still see the Japanese document name
 * rather than a translated approximation. Copy is verbatim from ACDEV-28461.
 */
const JAPAN_FOOTER_LINKS: FooterLinkConfigs = {
  refundPolicy: { url: 'https://www.appcharge.com/refund-policy-jp', label: '返金ポリシー' },
  cookiePolicy: { url: 'https://www.appcharge.com/cookie-policy-jp', label: 'Cookieポリシー' },
  // Act on Specified Commercial Transactions (特定商取引法) notice.
  scta: { url: 'https://www.appcharge.com/scta-jp', label: '特商法' },
};

const FOOTER_LINKS_BY_COUNTRY: Partial<Record<CountryCode, FooterLinkConfigs>> = {
  [CountryCode.JAPAN]: JAPAN_FOOTER_LINKS,
};

/**
 * Support sits outside the ordered list above (its href is per-customer) but its
 * label follows the same country rule: a country listed here pins fixed copy,
 * everyone else reads `checkout.support` from the active locale.
 */
const FOOTER_SUPPORT_LABELS: Partial<Record<CountryCode, string>> = {
  [CountryCode.JAPAN]: 'サポート',
};

export interface FooterLink extends FooterLinkConfig {
  labelKey: FooterLinkId;
}

/**
 * Resolves the ordered legal links for a country. `countryCode` is the
 * ISO-3166 alpha-2 used elsewhere (`US`, `JP`, ...), compared
 * case-insensitively. Unknown or empty countries fall back to the default set,
 * so every non-Japan session keeps its pre-ACDEV-28461 links.
 */
export function getFooterLinks(countryCode: string): FooterLink[] {
  const configs = FOOTER_LINKS_BY_COUNTRY[countryCode.toUpperCase() as CountryCode] ?? DEFAULT_FOOTER_LINKS;

  return FOOTER_LINK_ORDER.reduce<FooterLink[]>((links, labelKey) => {
    const config = configs[labelKey];
    return config ? [...links, { labelKey, ...config }] : links;
  }, []);
}

/**
 * The support link's pinned label for a country, or `undefined` when the label
 * should come from the active locale. Same country matching as
 * {@link getFooterLinks}.
 */
export function getFooterSupportLabel(countryCode: string): string | undefined {
  return FOOTER_SUPPORT_LABELS[countryCode.toUpperCase() as CountryCode];
}
